import React from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_PREFIX = "gl26_dur:";

const getAuthQuery = () => {
  if (typeof window === "undefined") return "";
  const t = window.localStorage.getItem("gl26_auth_token");
  return t ? `?t=${encodeURIComponent(t)}` : "";
};

/**
 * Reads audio durations for a list of filenames (audio/ folder).
 * Uses HTMLAudioElement with preload="metadata" so only the header
 * is downloaded. Results are cached in localStorage by filename.
 *
 * Returns: { [filename]: number_seconds, ... }
 *  - undefined  -> still loading
 *  - 0          -> couldn't read (file missing / unsupported)
 *  - >0         -> duration in seconds
 */
export const useAudioDurations = (filenames) => {
  const [durations, setDurations] = React.useState({});

  // Stable signature so the effect doesn't re-run on every render
  const key = React.useMemo(
    () => filenames.filter(Boolean).sort().join("|"),
    [filenames]
  );

  React.useEffect(() => {
    const list = filenames.filter(Boolean);
    const probes = [];

    list.forEach((f) => {
      if (durations[f] !== undefined) return;

      const cached = localStorage.getItem(STORAGE_PREFIX + f);
      if (cached !== null) {
        const n = parseFloat(cached);
        if (!Number.isNaN(n)) {
          setDurations((d) => ({ ...d, [f]: n }));
          return;
        }
      }

      const a = new Audio();
      a.preload = "metadata";
      a.src = `${API}/files/audio/${f}${getAuthQuery()}`;

      const onLoaded = () => {
        const dur = a.duration;
        const safe = Number.isFinite(dur) && dur > 0 ? dur : 0;
        try {
          localStorage.setItem(STORAGE_PREFIX + f, String(safe));
        } catch (_) { /* quota */ }
        setDurations((d) => ({ ...d, [f]: safe }));
        cleanup();
      };
      const onError = () => {
        setDurations((d) => ({ ...d, [f]: 0 }));
        cleanup();
      };
      const cleanup = () => {
        a.removeEventListener("loadedmetadata", onLoaded);
        a.removeEventListener("error", onError);
        a.src = "";
      };

      a.addEventListener("loadedmetadata", onLoaded);
      a.addEventListener("error", onError);
      probes.push(cleanup);
    });

    return () => probes.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return durations;
};

export const formatDuration = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return "—:—";
  const s = Math.round(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }
  return `${m}:${String(sec).padStart(2, "0")}`;
};

/**
 * Parse a user-entered duration ("mm:ss", "h:mm:ss", "3.5", "210") into seconds.
 * Returns null when the input is empty/invalid.
 */
export const parseDurationInput = (raw) => {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // Pure number (seconds or minutes? assume seconds when integer >= 60, minutes if decimal & <60)
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }
  // h:mm:ss or mm:ss
  const parts = s.split(":").map((p) => p.trim());
  if (parts.length > 3 || parts.some((p) => !/^\d+$/.test(p))) return null;
  const nums = parts.map(Number);
  if (nums.some((n) => !Number.isFinite(n))) return null;
  if (parts.length === 3) return nums[0] * 3600 + nums[1] * 60 + nums[2];
  if (parts.length === 2) return nums[0] * 60 + nums[1];
  return null;
};

/**
 * Pick the best duration for a song:
 *   1. song.duration_seconds (manual override) — always wins when > 0
 *   2. probedDurations[song.audio_*_filename]   — auto-detected fallback
 * Returns 0 when nothing is known.
 */
export const resolveSongDuration = (song, probedDurations) => {
  if (!song) return 0;
  if (song.duration_seconds && song.duration_seconds > 0) {
    return Number(song.duration_seconds);
  }
  const probed = probedDurations || {};
  const fname =
    song.audio_playback_filename ||
    song.audio_repete_filename ||
    song.audio_filename;
  const d = fname ? probed[fname] : 0;
  return d && d > 0 ? d : 0;
};
